import { CommentOutlined, MailOutlined, SendOutlined } from '@ant-design/icons';
import { useViewActions } from '@mm/state/actions/viewActions';

import WidgetModel, {
  EmailConfig,
  NavigateConfig,
  WidgetTypes,
} from '@mm/state/model/WidgetModel';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';

import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as SmsIcon } from './phone.svg';
import classNames from 'classnames';
import React, { useEffect, useMemo } from 'react';
import { Handle, NodeProps, Position } from 'react-flow-renderer';
import { useSelector } from 'react-redux';
import { useCommonWidgetHandlers } from '../../../../../../../../../common/helpers/widget-helpers/use-common-widget-handlers';

import { ITime } from '../../../../../../../../components/SlideoutMenu/menus/SlideoutDateTime/SlideoutDateTime';
import { Date } from '../elements/Date/Date';

import common from '../index.module.css';
import css from './Dialogue.module.css';
import { Count } from '../elements/Count/Count';
import { useOpenOnReady } from '../use-open-on-ready';
import { useDialoguePreviewMessage } from './use-dialogue-preview-message';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';

type Props = { className?: string } & NodeProps<{
  widget: WidgetModel;
}>;
const VALID_PLATFORMS = ['sms', 'email'] as const;
export const DialogueIcon = CommentOutlined;

export const SMSDialogueIcon = SmsIcon;
export const EmailDialogueIcon = MailOutlined;

export const Dialogue: React.FC<Props> = ({
  className,
  data,
  isDragging,
  children,
}) => {
  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const widget = widgets.find(w => w.id === data.widget.id) || data.widget;

  const config = widget.config as NavigateConfig;

  const dialogueId = widget.config.pageId;

  const page = useSelector(state =>
    pageEntityByIdSelector(state, dialogueId?.toString() || ''),
  );

  const platform = VALID_PLATFORMS.find(p => page?.platforms?.includes(p));
  const isEmail = platform === 'email';

  const emailWidget = useMemo(
    () =>
      page && isEmail
        ? page.widgets?.find(w => w.type === WidgetTypes.EMAIL) ||
          widgets.find(
            w => w.pageId === dialogueId && w.type === WidgetTypes.EMAIL,
          )
        : undefined,
    [page, isEmail, widgets],
  );

  const emailWidgetConfig = emailWidget?.config as unknown as EmailConfig;

  const commonWidgetHandlers = useCommonWidgetHandlers();

  const chatbotId = useSelector(activeChatbotIdSelector);

  const placeholder = config.sendNextAt ? 'Wait until' : 'Wait for';

  const time: ITime = useMemo(() => {
    return {
      date: config.sendNextAt,
      value: config.sendNextAfterValue,
      measure: config.sendNextAfterMeasure,
    };
  }, [
    config.sendNextAt,
    config.sendNextAfterValue,
    config.sendNextAfterMeasure,
  ]);

  const firstMessage = useDialoguePreviewMessage({ dialogueId });

  const viewActions = useViewActions();

  const onClick = () => {
    console.debug(`Dialogue: onClick: `, { data, widget });
    if (isDragging) {
      return;
    }

    if (isEmail) {
      return viewActions.showSlideoutMenu('email', {
        chatbotId,
        navigateWidget: widget,
        dialogueId: widget.config.pageId,
      });
    }

    viewActions.showSlideoutMenu('editor', {
      chatbotId,
      dialogueId,
    });
  };

  const onEmailStatsClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    console.debug(`Dialogue: onEmailStatsClick: `, { data, widget });

    if (isDragging) {
      return;
    }
    e.stopPropagation();

    return viewActions.showSlideoutMenu('emailStats', {
      chatbotId,
      dialogueId,
      emailWidgetId: emailWidget?.id,
    });
  };

  useOpenOnReady(widget, onClick);

  const onSubmit = async ({ value, measure, date }: ITime, closeModal) => {
    console.debug(`Dialogue: onSubmit: `, { value, measure });

    if (isDragging) {
      return;
    }

    try {
      await commonWidgetHandlers.update({
        ...widget,
        config: {
          ...widget.config,
          sendNextAt: date,
          sendNextAfterValue: value,
          sendNextAfterMeasure: measure,
        } as any,
      });
      closeModal();
    } catch (err) {}
  };

  const onDateClick = () => {
    console.debug(`Dialogue: onDateClick: `, { data, widget });
    viewActions.showSlideoutMenu('dateTime', {
      title: 'Wait',
      onSubmit,
      time: {
        date: config.sendNextAt,
        value: config.sendNextAfterValue,
        measure: config.sendNextAfterMeasure,
      },
    });
  };

  const visitedCount = page?.viewsCount;

  const widgetsCRUDActions = useWidgetCRUDActions();

  useEffect(() => {
    if (isEmail && dialogueId && !emailWidget) {
      widgetsCRUDActions.retrieveAll(dialogueId);
    }
  }, []);

  return (
    <div className={css.wrapper}>
      <Handle type="target" position={Position.Top} />
      <div
        className={classNames(className, common.card, css.container)}
        onClick={onClick}
      >
        <div className={css.body}>
          {children}

          <div className={css.header}>
            <div className={css.icon}>
              {!platform && <DialogueIcon />}
              {platform === 'sms' && <SmsIcon />}
              {isEmail && <EmailDialogueIcon />}
            </div>

            <div className={css.headerText}>
              <span className={css.placeholder}>{page?.name || '...'}</span>
              {(platform === 'sms' || platform === 'email') && firstMessage && (
                <span className={css.text}>{firstMessage}</span>
              )}
            </div>
          </div>

          {isEmail && emailWidgetConfig ? (
            <Count
              className={css.emailStat}
              icon={<SendOutlined />}
              value={`${Intl.NumberFormat().format(
                emailWidgetConfig.emailSendsCount || 0,
              )} sent`}
              onClick={onEmailStatsClick}
            />
          ) : (
            <Count className={css.stat} value={`${visitedCount || 0} viewed`} />
          )}
        </div>
      </div>

      <Date
        className={css.date}
        placeholder={placeholder}
        awaitingCount={config?.delaysCount}
        time={time}
        onClick={onDateClick}
      />

      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};
